import React, { useState, useEffect } from 'react';
import { 
  Database, Download, Upload, Cloud, HardDrive, 
  AlertTriangle, CheckCircle, Clock, Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function BackupManagement() {
  const [backups, setBackups] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [includeCloud, setIncludeCloud] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch backups list
      const backupsRes = await fetch(`${API_URL}/api/backup/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (backupsRes.ok) {
        const data = await backupsRes.json();
        setBackups(data);
      }

      // Fetch backup jobs history
      const jobsRes = await fetch(`${API_URL}/api/backup/jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (jobsRes.ok) {
        const data = await jobsRes.json();
        setJobs(data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
    }
  };

  const createBackup = async () => {
    if (!window.confirm('Creare un nuovo backup? Questa operazione può richiedere alcuni minuti.')) {
      return;
    }

    setCreating(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/backup/create?include_cloud=${includeCloud}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore creazione backup');

      const result = await res.json();

      if (result.status === 'success') {
        alert('✅ Backup completato con successo!');
      } else if (result.status === 'partial') {
        alert('⚠️ Backup parziale completato. Controlla i log per dettagli.');
      } else {
        alert('❌ Backup fallito. Controlla i log.');
      }

      fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (backupName) => {
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/backup/download/${backupName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore download');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backupName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteBackup = async (backupName) => {
    if (!window.confirm('Eliminare questo backup? Azione irreversibile.')) {
      return;
    }

    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/backup/${backupName}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore eliminazione');

      alert('Backup eliminato');
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  const restoreBackup = async (backupName) => {
    const confirmed = window.confirm(
      '⚠️ ATTENZIONE: Ripristinare un backup sostituirà TUTTI i dati attuali. ' +
      'Questa operazione è irreversibile. Sei sicuro di voler procedere?'
    );

    if (!confirmed) return;

    const doubleConfirm = window.prompt(
      'Digita "RIPRISTINA" per confermare:',
      ''
    );

    if (doubleConfirm !== 'RIPRISTINA') {
      alert('Operazione annullata');
      return;
    }

    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/backup/restore/${backupName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore restore');

      const result = await res.json();

      if (result.status === 'success') {
        alert('✅ Restore completato con successo! Ricarica la pagina.');
        window.location.reload();
      } else {
        alert('⚠️ Restore parziale o fallito. Controlla i log.');
      }
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento backup...</p>
      </div>
    );
  }

  return (
    <div data-testid="backup-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <Database className="inline mr-2" size={32} />
            Backup & Restore
          </h1>
          <p className="page-description">
            Gestisci backup del database e dei file
          </p>
        </div>
      </div>

      {/* Create Backup Section */}
      <div className="card mb-6 max-w-2xl">
        <h3 className="text-lg font-semibold mb-4">Crea Nuovo Backup</h3>
        
        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeCloud}
              onChange={(e) => setIncludeCloud(e.target.checked)}
              data-testid="include-cloud"
            />
            <Cloud size={18} />
            <span>Carica anche su cloud storage (se configurato)</span>
          </label>
          <p className="text-xs text-muted-foreground ml-7 mt-1">
            Il backup locale sarà sempre creato. Il cloud è opzionale.
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={createBackup}
          disabled={creating}
          data-testid="create-backup-button"
        >
          <HardDrive size={18} className="mr-2" />
          {creating ? 'Creazione in corso...' : 'Crea Backup Ora'}
        </button>
      </div>

      {/* Backups List */}
      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4">Backup Disponibili</h3>

        {backups.length === 0 ? (
          <div className="text-center py-8">
            <Database className="mx-auto text-muted-foreground mb-3" size={48} />
            <p className="text-muted-foreground">Nessun backup disponibile</p>
          </div>
        ) : (
          <div className="table-container">
            <table data-testid="backups-table">
              <thead>
                <tr>
                  <th>Nome Backup</th>
                  <th>Data Creazione</th>
                  <th>Dimensione</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.name} data-testid="backup-row">
                    <td className="font-mono text-sm">{backup.name}</td>
                    <td>
                      {format(new Date(backup.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                    </td>
                    <td>{backup.size_mb.toFixed(2)} MB</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => downloadBackup(backup.name)}
                          data-testid="download-backup"
                        >
                          <Download size={16} />
                          Download
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => restoreBackup(backup.name)}
                          data-testid="restore-backup"
                        >
                          <Upload size={16} />
                          Ripristina
                        </button>
                        <button
                          className="btn btn-secondary btn-sm text-destructive"
                          onClick={() => deleteBackup(backup.name)}
                          data-testid="delete-backup"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Backup Jobs History */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Storico Operazioni</h3>

        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna operazione registrata</p>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <div
                key={job._id}
                className="border border-border rounded-lg p-3"
                data-testid="backup-job"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {job.status === 'success' && (
                        <CheckCircle size={18} className="text-green-600" />
                      )}
                      {job.status === 'partial' && (
                        <AlertTriangle size={18} className="text-yellow-600" />
                      )}
                      {job.status === 'failed' && (
                        <AlertTriangle size={18} className="text-red-600" />
                      )}
                      <span className="font-medium">
                        {job.type === 'restore' ? 'Restore' : 'Backup'}
                      </span>
                      {job.backup_name && (
                        <span className="text-sm text-muted-foreground font-mono">
                          {job.backup_name}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <Clock size={14} className="inline mr-1" />
                      {format(new Date(job.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                      {job.size_mb && (
                        <span className="ml-3">• {job.size_mb.toFixed(2)} MB</span>
                      )}
                    </div>
                    {job.errors && job.errors.length > 0 && (
                      <div className="mt-2 text-xs text-destructive">
                        Errori: {job.errors.join(', ')}
                      </div>
                    )}
                  </div>
                  {job.status === 'success' && (
                    <span className="badge badge-ok">Completato</span>
                  )}
                  {job.status === 'partial' && (
                    <span className="badge badge-warning">Parziale</span>
                  )}
                  {job.status === 'failed' && (
                    <span className="badge badge-critical">Fallito</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BackupManagement;
